/** Exact live asset choices shared from a page to its controlling service worker. */
import {
  coverKey,
  entityCoverKey,
  textureKey,
  toCacheRequestUrl,
  waveformKey,
} from './offlineKeys.js';
import { resolveWorldTextureUrls } from '../world/worldTextureUrls.js';
import {
  ASSET_BINDINGS_MESSAGE,
  ASSET_BINDINGS_NEEDED_MESSAGE,
} from './offlineAssetIndex.js';

const scopes = new Map();
let nextScope = 0;
let listening = false;

function normalize(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text || null;
}

/** The cover tier used by both downloads and live binding. */
export function resolveTrackCoverUrl(track) {
  const direct = normalize(track?.artworkURL);
  if (direct) return direct;
  const images = track?.images;
  if (!images || typeof images !== 'object') return null;
  const square = images['square-cover'] || images.cover || null;
  if (!square || typeof square !== 'object') return null;
  return normalize(square.mid) || normalize(square.small) || normalize(square.original);
}

function binding(url, identity) {
  const resolvedUrl = normalize(url);
  const key = toCacheRequestUrl(identity);
  return resolvedUrl && key ? { url: resolvedUrl, key } : null;
}

function blockedBinding(url) {
  const resolvedUrl = normalize(url);
  return resolvedUrl ? { url: resolvedUrl, blocked: true } : null;
}

/** Assets whose exact identity is known after a track/world configuration resolves. */
export function resolvedReleaseAssetBindings({ track, entangledWorld } = {}) {
  const bindings = [];
  const trackId = normalize(track?.trackId);
  const trackVersion = normalize(track?.version);
  if (trackId && trackVersion) {
    const waveform = binding(
      track?.waveformJSONURL,
      waveformKey({ trackId, version: trackVersion }),
    );
    const cover = binding(
      resolveTrackCoverUrl(track),
      coverKey({ trackId, version: trackVersion }),
    );
    if (waveform) bindings.push(waveform);
    if (cover) bindings.push(cover);
  } else if (trackId) {
    const waveform = blockedBinding(track?.waveformJSONURL);
    const cover = blockedBinding(resolveTrackCoverUrl(track));
    if (waveform) bindings.push(waveform);
    if (cover) bindings.push(cover);
  }

  const worldId = normalize(entangledWorld?.worldId);
  const worldVersion = normalize(entangledWorld?.version);
  if (worldId && worldVersion) {
    for (const url of resolveWorldTextureUrls(entangledWorld)) {
      const texture = binding(url, textureKey({ worldId, version: worldVersion, url }));
      if (texture) bindings.push(texture);
    }
  } else if (worldId) {
    for (const url of resolveWorldTextureUrls(entangledWorld)) {
      const texture = blockedBinding(url);
      if (texture) bindings.push(texture);
    }
  }
  return bindings;
}

export function collectionAssetBindings(collection) {
  const bindings = Array.isArray(collection?.offlineAssetBindings)
    ? [...collection.offlineAssetBindings]
    : [];
  for (const entry of collection?.roster || []) {
    const kind = normalize(entry?.entityType)?.toLowerCase();
    if (kind !== 'orbiter' && kind !== 'world' && kind !== 'entangled-world') continue;
    const entityType = kind === 'orbiter' ? 'orbiter' : 'world';
    const entityId = normalize(entityType === 'orbiter' ? entry?.orbiterId : entry?.entangledWorldId);
    const version = normalize(entry?.hydrated?.release?.version ?? entry?.hydrated?.version);
    const item = binding(
      entry?.image,
      entityCoverKey({ entityType, entityId, version }),
    ) || blockedBinding(entry?.image);
    if (item) bindings.push(item);
  }
  return bindings;
}

export function createOfflineAssetBindingScope() {
  nextScope += 1;
  return `asset-scope:${nextScope}`;
}

function controller() {
  return typeof navigator !== 'undefined' ? navigator.serviceWorker?.controller ?? null : null;
}

function combinedBindings() {
  const byUrl = new Map();
  for (const bindings of scopes.values()) {
    for (const item of bindings) {
      if (!byUrl.has(item.url)) byUrl.set(item.url, { exact: new Set(), stored: new Set(), blocked: false });
      const group = byUrl.get(item.url);
      if (item.blocked) group.blocked = true;
      else (item.stored ? group.stored : group.exact).add(item.key);
    }
  }
  return [...byUrl].map(([url, keys]) => ({
    url,
    // During an offline boot, the manifest's stored key is the exact representation selected for
    // that collection. It also preserves pre-migration key shapes, so it takes precedence over the
    // key current code would generate for the same release.
    keys: [...(keys.stored.size ? keys.stored : keys.blocked ? [] : keys.exact)],
  }));
}

function postBindings() {
  const worker = controller();
  if (!worker) return Promise.resolve();
  try {
    if (typeof MessageChannel === 'undefined') {
      worker.postMessage({ type: ASSET_BINDINGS_MESSAGE, bindings: combinedBindings() });
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const channel = new MessageChannel();
      const timer = setTimeout(resolve, 1_000);
      channel.port1.onmessage = () => {
        clearTimeout(timer);
        resolve();
      };
      worker.postMessage(
        { type: ASSET_BINDINGS_MESSAGE, bindings: combinedBindings() },
        [channel.port2],
      );
    });
  } catch {
    // Missing bindings only disable the optimization; the route remains network-first.
    return Promise.resolve();
  }
}

function listenForWorker() {
  if (listening || typeof navigator === 'undefined' || !navigator.serviceWorker) return;
  listening = true;
  navigator.serviceWorker.addEventListener?.('message', (event) => {
    if (event.data?.type === ASSET_BINDINGS_NEEDED_MESSAGE) postBindings();
  });
  navigator.serviceWorker.addEventListener?.('controllerchange', postBindings);
}

/** Replace one runtime owner's complete asset set and publish the union for this page. */
export function replaceOfflineAssetBindingScope(scope, bindings) {
  if (!scope) return Promise.resolve();
  const clean = (bindings || []).filter((item) => (
    normalize(item?.url) && (normalize(item?.key) || item?.blocked === true)
  ));
  scopes.set(scope, clean.map((item) => ({
    url: normalize(item.url),
    key: normalize(item.key),
    stored: item.stored === true,
    blocked: item.blocked === true,
  })));
  listenForWorker();
  return postBindings();
}

export function clearOfflineAssetBindingScope(scope) {
  scopes.delete(scope);
  postBindings();
}
